import React, { Component } from "react";
import { get as _get, cloneDeep as _cloneDeep, isEqual as _isEqual, isEmpty as _isEmpty } from "lodash";
import { FormattedMessage, injectIntl } from "react-intl";
import { connect } from "react-redux";
import moment from "moment";

import { CustomModal, showAlertMessage, ErrorMessage, DatePicker, Select } from "../common/formElements";
import { WeekDays } from "../../helpers/constants";
import { updateLoadingState } from "../../redux/actions/application";

import { getCurrencyList, getRateTypes } from "../../redux/services/campaignSetup";
import { updateCampaignDetails } from "../../redux/services/campaign";

class EditCampaignInfo extends Component {

  constructor(props) {
    super(props);

    this.defaultState = {
      campaignName: "",
      poNumber: "",
      currencySymbol: "",
      rateValue: "",
      rateType: "",
      campaignStartDate: "",
      campaignEndDate: "",
      uploadDay: "",
    }

    this.state = {
      formData: _cloneDeep(this.defaultState),
      currencyDropDownData: [],
      rateTypesDropDownData: [],
      isFormSubmitted: false,
      formErrors: {},
    };
  }

  componentDidMount() {
    this._fetchCurrencies();
    this._fetchRateTypes();
  }

  componentDidUpdate(prevProps, prevState) {
    if (!_isEqual(prevProps.showEditCampaignInfoModal, this.props.showEditCampaignInfoModal) && this.props.showEditCampaignInfoModal) {
      this._setFormData();
    }

    if (!_isEqual(prevState.formData, this.state.formData) && (_get(this.state, "isFormSubmitted", false) === true)) {
      this._handleValidation();
    }
  }

  _setFormData = () => {
    const { campaignDetails, firstBrief } = this.props;
    if (_isEmpty(campaignDetails) && _isEmpty(firstBrief)) return;

    const parseDateToISO = (dateStr) => {
      if (!dateStr) return "";
      // If it has slashes, it's likely DD/MM/YYYY HH:mm:ss
      if (dateStr.includes("/")) {
        const parsed = moment(dateStr, "DD/MM/YYYY HH:mm:ss");
        if (parsed.isValid()) return parsed.format();
        const parsed2 = moment(dateStr, "DD/MM/YYYY");
        if (parsed2.isValid()) return parsed2.format();
      }
      return dateStr;
    };

    const rawStartDate = _get(campaignDetails, "start_date") || _get(firstBrief, "start_date") || "";
    const rawEndDate = _get(campaignDetails, "end_date") || _get(firstBrief, "end_date") || "";

    this.setState({
      formData: {
        campaignName: _get(campaignDetails, "campaign_name") || _get(firstBrief, "campaign_name", ""),
        poNumber: _get(campaignDetails, "po_number") || _get(firstBrief, "po_number", ""),
        currencySymbol: _get(campaignDetails, "currency") || _get(firstBrief, "currency", ""),
        rateValue: (_get(campaignDetails, "rate") || _get(firstBrief, "rate", "")).toString(),
        rateType: _get(campaignDetails, "rate_type") || _get(firstBrief, "rate_type", ""),
        campaignStartDate: parseDateToISO(rawStartDate),
        campaignEndDate: parseDateToISO(rawEndDate),
        uploadDay: _get(campaignDetails, "upload_day") || _get(firstBrief, "upload_day", "")
      },
      formErrors: {},
      isFormSubmitted: false
    });
  }

  _handleValidation = (returnFlag = false) => {
    const { intl } = this.props;
    const { formData: { campaignName, poNumber, currencySymbol, rateValue, rateType, campaignStartDate, campaignEndDate, uploadDay } } = this.state;
    let formErrors = {};

    if (_isEmpty(campaignName)) {
      formErrors["campaignName"] = intl.formatMessage({ id: "error.required", defaultMessage: "{field} is required." }, {
        field: intl.formatMessage({ id: "campaign_setup.campaign_detail.campaign_label", defaultMessage: " campaign name" })
      });
    }

    if ((campaignStartDate || "") === "") {
      formErrors["campaignStartDate"] = intl.formatMessage({ id: "error.required", defaultMessage: "{field} is required." }, {
        field: intl.formatMessage({ id: "campaign_setup.campaign_detail.campaign_start_date", defaultMessage: "campaign start date" })
      });
    }

    if ((campaignEndDate || "") === "") {
      formErrors["campaignEndDate"] = intl.formatMessage({ id: "error.required", defaultMessage: "{field} is required." }, {
        field: intl.formatMessage({ id: "campaign_setup.campaign_detail.campaign_end_date", defaultMessage: "campaign end date" })
      });
    } else if (moment(campaignEndDate).isBefore(campaignStartDate, "day")) {
      formErrors["campaignEndDate"] = intl.formatMessage({ id: "error.after_start_date", defaultMessage: "End date field should be after start date." });
    }

    if (_isEmpty(poNumber)) {
      formErrors["poNumber"] = intl.formatMessage({ id: "error.required", defaultMessage: "{field} is required." }, {
        field: intl.formatMessage({ id: "campaign_setup.campaign_detail.po_number_label", defaultMessage: "PO number" })
      });
    }

    if (_isEmpty(currencySymbol)) {
      formErrors["currencySymbol"] = intl.formatMessage({ id: "error.required", defaultMessage: "{field} is required." }, {
        field: intl.formatMessage({ id: "campaign_setup.campaign_detail.currency", defaultMessage: "currency" })
      });
    }

    if (!rateValue || rateValue.toString().trim() === "") {
      formErrors["rateValue"] = intl.formatMessage({ id: "error.required", defaultMessage: "{field} is required." }, {
        field: intl.formatMessage({ id: "campaign_setup.campaign_detail.rate", defaultMessage: "rate" })
      });
    }

    if (rateValue && parseFloat(rateValue) <= 0) {
      formErrors["rateValue"] = intl.formatMessage({ id: "error.greater_than_value", defaultMessage: "{field} should be greater than {value}." }, {
        field: intl.formatMessage({ id: "campaign_setup.campaign_detail.rate", defaultMessage: "rate" }), value: 0
      });
    }

    const regexRateValidation = /^(\d{8}|\d{1,8}(\.\d{1,2})?)$/;
    if (rateValue && regexRateValidation.test(rateValue.toString()) === false) {
      formErrors["rateValue"] = intl.formatMessage({ id: "error.invalid_rate_value", defaultMessage: "rate should be a number with up to 2 decimal points, less than 8 digits." });
    }

    if (_isEmpty(rateType)) {
      formErrors["rateType"] = intl.formatMessage({ id: "error.required", defaultMessage: "{field} is required." }, {
        field: intl.formatMessage({ id: "campaign_setup.campaign_detail.rate_type", defaultMessage: "rate type" })
      });
    }

    if (_isEmpty(uploadDay)) {
      formErrors["uploadDay"] = intl.formatMessage({ id: "error.required", defaultMessage: "{field} is required." }, {
        field: intl.formatMessage({ id: "campaign_setup.campaign_detail.upload_day", defaultMessage: "upload day" })
      });
    }

    this.setState({ formErrors });

    if (returnFlag) {
      return (!_isEmpty(formErrors)) ? false : true;
    }
  }

  handleChange = (keyName, keyValue) => {
    this.setState(prevState => ({
      ...prevState,
      formData: {
        ...prevState.formData,
        [keyName]: keyValue,
      }
    }));
  }

  _fetchCurrencies = async () => {
    try {
      const response = await getCurrencyList();
      if ((response.flag || false) === true) {
        this.setState({ currencyDropDownData: _get(response, "data.responseData", []) });
      }
    } catch (error) {
      //
    }
  };

  _fetchRateTypes = async () => {
    try {
      const response = await getRateTypes();
      if ((response.flag || false) === true) {
        this.setState({ rateTypesDropDownData: _get(response, "data.responseData", []) });
      }
    } catch (error) {
      //
    }
  };

  _handleSubmit = async () => {
    const { campaignId, campaignDetails, firstBrief, updateLoadingState, setState } = this.props;
    const { formData: { campaignName, poNumber, currencySymbol, rateValue, rateType, campaignStartDate, campaignEndDate, uploadDay } } = this.state;

    this.setState({ isFormSubmitted: true });
    if (!this._handleValidation(true)) { return false; }

    try {
      if (typeof updateLoadingState === "function") { updateLoadingState(true); }

      const payload = {
        campaign_id: campaignId,
        campaign_name: campaignName,
        client_id: _get(campaignDetails, "client_id") || _get(firstBrief, "client_id", null),
        start_date: campaignStartDate,
        end_date: campaignEndDate,
        po_number: poNumber,
        currency: currencySymbol,
        rate: rateValue,
        rate_type: rateType,
        upload_day: uploadDay
      };

      const response = await updateCampaignDetails(payload);

      if ((response.flag || false) === true) {
        showAlertMessage(_get(response, "message", "Campaign details updated successfully."), "success");
        if (typeof setState === "function") {
          setState({ showEditCampaignInfoModal: false, reloadBriefList: true });
        }
      } else {
        showAlertMessage(_get(response, "message", "Something went wrong while updating campaign details."));
      }

    } catch (err) {
      showAlertMessage(_get(err, "message", "Something went wrong while updating campaign details."));
    } finally {
      if (typeof updateLoadingState === "function") { updateLoadingState(false); }
    }
  };

  _handleClose = () => {
    const { setState } = this.props;
    if (typeof setState === "function") {
      setState({ showEditCampaignInfoModal: false });
    }
  }

  _renderTextFiled = ({ label, name, defaultValue, onChange, placeholder, isRequired = false, type = "text" }) => {
    return (
      <>
        <label className="form-label text-capitalize">
          {label}
          {(isRequired) && (<span className="text-danger">*</span>)}
        </label>
        <input
          type={type}
          className="form-control"
          name={(name || "")}
          placeholder={(placeholder || "")}
          value={(defaultValue || "")}
          onChange={onChange} />
      </>
    );
  }

  render() {
    const { showEditCampaignInfoModal, intl, displayDateFormat } = this.props;
    const { currencyDropDownData, rateTypesDropDownData, formErrors,
      formData: { campaignName, poNumber, currencySymbol, rateValue, rateType, campaignStartDate, campaignEndDate, uploadDay } }
      = this.state;

    const selectedCurrency = currencyDropDownData.find(c => c.symbol === currencySymbol) || (currencySymbol ? { symbol: currencySymbol, name: currencySymbol } : null);
    const selectedRateType = rateTypesDropDownData.find(r => r.rate_type === rateType) || (rateType ? { rate_type: rateType, id: rateType } : null);
    const selectedUploadDay = WeekDays.find(w => w.value === uploadDay) || (uploadDay ? { value: uploadDay, label: uploadDay } : null);

    return (
      <CustomModal
        isOpen={(showEditCampaignInfoModal || false)}
        modalTitle={intl.formatMessage({ id: "btn.edit_campaign_info", defaultMessage: "Edit Campaign Info" })}
        onClose={this._handleClose}
        size="xl"
      >
        <div className="row">
          <div className="col-lg-4">
            <div className="mb-6">
              {this._renderTextFiled({
                label: intl.formatMessage({ id: "campaign_setup.campaign_detail.campaign_label", defaultMessage: "campaign name" }),
                name: "campaignName",
                defaultValue: (campaignName || ""),
                onChange: (event) => this.handleChange("campaignName", _get(event, "target.value", "")),
                placeholder: intl.formatMessage({ id: "placeholder.enter_field", defaultMessage: "Enter {field} " }, {
                  field: intl.formatMessage({ id: "campaign_setup.campaign_detail.campaign_label", defaultMessage: "campaign name" })
                }),
                isRequired: true
              })}
              {_get(formErrors, "campaignName", "") && <ErrorMessage message={_get(formErrors, "campaignName", "")} />}
            </div>
          </div>

          <div className="col-lg-4">
            <div className="mb-6">
              <label className="form-label text-capitalize">
                <FormattedMessage id="campaign_setup.campaign_detail.campaign_start_date" defaultMessage="campaign start date" />
                <span className="text-danger">*</span>
              </label>
              <DatePicker
                className="form-control"
                selected={(campaignStartDate ? moment(campaignStartDate).toDate() : null)}
                placeholderText={intl.formatMessage({ id: "campaign_setup.campaign_detail.assign.start_date_placeholder", defaultMessage: "Choose a start date" })}
                onChange={(e) => this.handleChange("campaignStartDate", moment(e).format())}
                dateFormat={displayDateFormat || "dd/MM/yyyy"}
              />
              {_get(formErrors, "campaignStartDate", "") && <ErrorMessage message={_get(formErrors, "campaignStartDate", "")} />}
            </div>
          </div>

          <div className="col-lg-4">
            <div className="mb-6">
              <label className="form-label text-capitalize">
                <FormattedMessage id="campaign_setup.campaign_detail.campaign_end_date" defaultMessage="campaign end date" />
                <span className="text-danger">*</span>
              </label>
              <DatePicker
                className="form-control"
                selected={(campaignEndDate ? moment(campaignEndDate).toDate() : null)}
                placeholderText={intl.formatMessage({ id: "campaign_setup.campaign_detail.assign.end_date_placeholder", defaultMessage: "Choose an end date" })}
                onChange={(e) => this.handleChange("campaignEndDate", moment(e).format())}
                dateFormat={displayDateFormat || "dd/MM/yyyy"}
                minDate={(campaignStartDate ? moment(campaignStartDate).toDate() : null)}
              />
              {_get(formErrors, "campaignEndDate", "") && <ErrorMessage message={_get(formErrors, "campaignEndDate", "")} />}
            </div>
          </div>

          <div className="col-lg-4">
            <div className="mb-6">
              {this._renderTextFiled({
                label: intl.formatMessage({ id: "campaign_setup.campaign_detail.po_number_label", defaultMessage: "PO number" }),
                name: "poNumber",
                defaultValue: (poNumber || ""),
                onChange: (event) => this.handleChange("poNumber", _get(event, "target.value", "")),
                placeholder: intl.formatMessage({ id: "placeholder.enter_field", defaultMessage: "Enter {field} " }, {
                  field: intl.formatMessage({ id: "campaign_setup.campaign_detail.po_number_label", defaultMessage: "PO number" })
                }),
                isRequired: true,
              })}
              {_get(formErrors, "poNumber", "") && <ErrorMessage message={_get(formErrors, "poNumber", "")} />}
            </div>
          </div>

          <div className="col-lg-4">
            <div className="mb-6">
              <label className="form-label text-capitalize">
                <FormattedMessage id="campaign_setup.campaign_detail.currency" defaultMessage="currency" />
                <span className="text-danger">*</span>
              </label>
              <Select
                className="form-custom-select"
                placeholder={intl.formatMessage({ id: "placeholder.select", defaultMessage: "select" })}
                options={(currencyDropDownData || [])}
                value={(selectedCurrency || null)}
                getOptionLabel={(option) => (option.name || "")}
                getOptionValue={(option) => (option.symbol || null)}
                onChange={(selected) => this.handleChange("currencySymbol", _get(selected, "symbol", ""))}
                isMulti={false}
              />
              {_get(formErrors, "currencySymbol", "") && <ErrorMessage message={_get(formErrors, "currencySymbol", "")} />}
            </div>
          </div>

          <div className="col-lg-4">
            <div className="mb-6">
              {this._renderTextFiled({
                label: intl.formatMessage({ id: "campaign_setup.campaign_detail.rate", defaultMessage: "rate" }),
                name: "rateValue",
                defaultValue: (rateValue || ""),
                onChange: (event) => this.handleChange("rateValue", _get(event, "target.value", "")),
                placeholder: intl.formatMessage({ id: "placeholder.enter_field", defaultMessage: "Enter {field}" }, {
                  field: intl.formatMessage({ id: "campaign_setup.campaign_detail.rate", defaultMessage: "rate" })
                }),
                isRequired: true,
                type: "number"
              })}
              {_get(formErrors, "rateValue", "") && <ErrorMessage message={_get(formErrors, "rateValue", "")} />}
            </div>
          </div>

          <div className="col-lg-4">
            <div className="mb-6">
              <label className="form-label text-capitalize">
                <FormattedMessage id="campaign_setup.campaign_detail.rate_type" defaultMessage="rate type" />
                <span className="text-danger">*</span>
              </label>
              <Select
                className="form-custom-select"
                placeholder={intl.formatMessage({ id: "placeholder.select", defaultMessage: "select" })}
                options={(rateTypesDropDownData || [])}
                value={(selectedRateType || null)}
                getOptionLabel={(option) => (option.rate_type || "")}
                getOptionValue={(option) => (option.id || null)}
                onChange={(selected) => this.handleChange("rateType", _get(selected, "rate_type", ""))}
                isMulti={false}
              />
              {_get(formErrors, "rateType", "") && <ErrorMessage message={_get(formErrors, "rateType", "")} />}
            </div>
          </div>

          <div className="col-lg-4">
            <div className="mb-6">
              <label className="form-label text-capitalize">
                <FormattedMessage id="campaign_setup.campaign_detail.upload_day" defaultMessage="upload day" />
                <span className="text-danger">*</span>
              </label>
              <Select
                className="form-custom-select"
                placeholder={intl.formatMessage({ id: "campaign_setup.campaign_detail.upload_day", defaultMessage: "upload day" })}
                options={(WeekDays || [])}
                value={(selectedUploadDay || null)}
                onChange={(selected) => this.handleChange("uploadDay", _get(selected, "value", ""))}
                isMulti={false}
              />
              {_get(formErrors, "uploadDay", "") && <ErrorMessage message={_get(formErrors, "uploadDay", "")} />}
            </div>
          </div>
        </div>

        <div className="pt-4 px-4 mx-n4 d-flex gap-3 align-items-center justify-content-end border-top">
          <button type="button" className="btn btn-outline-primary" onClick={this._handleClose}>
            <FormattedMessage id="btn.cancel" defaultMessage="Cancel" />
          </button>
          <button type="button" className="btn btn-primary" onClick={this._handleSubmit}>
            <FormattedMessage id="btn.submit" defaultMessage="Submit" />
          </button>
        </div>
      </CustomModal>
    );
  }
}

const mapStateToProps = (state) => ({
  displayDateFormat: _get(state, "application.constants.displayDateFormat", "dd/MM/yyyy")
});

const mapDispatchToProps = (dispatch) => ({
  updateLoadingState: (data) => dispatch(updateLoadingState(data))
});

export default connect(mapStateToProps, mapDispatchToProps)(injectIntl(EditCampaignInfo));
